import { useCallback, useMemo, useState } from "react";
import { number, object, node } from "prop-types";
import { observer } from "mobx-react";
import { BreakdownColumns } from "@repo/utils";
import {
  useSectionColumnWidthClasses,
  useEstimateEditorSettings,
  useUserVisibleWorkTypes,
  EstimateTableSettingsProvider,
  TableContainer,
  SectionColumnSettings,
} from "@tools";
import { Grid } from "@material-ui/core";
import useTableStyle from "@tools/EstimateEditor/styles/CommonTableStyles.style";
import classnames from "classnames";

export const SectionContainer = observer(({
  index,
  section,
  headerRef,
  children,
}) => {
  const { isPdfPrinterView, isProposal, forceExpansionArrows } = useEstimateEditorSettings();
  const tableClasses = useTableStyle();
  
  const [ artificialLabelColumnWidth, setArtificialLabelColumnWidth]  = useState(false);
  const [ showActionsOnScroll, setShowActionsOnScroll]  = useState(false);

  const {
    taskWorkTypes,
    sectionModulesHaveChildren,
    showCombinedHours,
    materialFirst,
    // hidePrice,
    // hideDescriptions,
    // hideLabels,
    hasMaterialCell,
    unitsName,
    unitPriceName,
    visibleBreakdownColumns,
    usedDescriptionColumns,
    // descriptionVisible,
    showNumerationColumn,
    showBreakdownLabelsColumn,
    displayWorkloadTwoValues,
    displayWorkTypeTwoValues,
    allDescriptionColumnsHidden,
    lastPinnedCellLeft,
    unpinnedColumnsWidth,
  } = section;
  
  const visibleWorkTypes = useUserVisibleWorkTypes(taskWorkTypes, isProposal);
  // const priceClassName = useVisibilityModeCellClassName(hidePrice);
  // const descriptionClassName = useMemo(() => (
  //   hideAdditionalDescription
  //     ? mapValues(Object.fromEntries(hideAdditionalDescription), (hide) => (
  //       visibilityMode && hide && "transparent-2"
  //     ))
  //     : {}
  // ), [ visibilityMode, hideAdditionalDescription?.size ]);
  
  // const labelsClassName = useVisibilityModeCellClassName(hideLabels);
  
  const tableContainerIdentifier = useMemo(() => `breakdownContainer${index}`, [index]);
  
  const useExpandCell = useMemo(() => (
    !isProposal || sectionModulesHaveChildren || forceExpansionArrows
  ), [isProposal, sectionModulesHaveChildren]);
  
  const colClasses = useSectionColumnWidthClasses(visibleBreakdownColumns)();
  
  const handleUnitsNameChange = useCallback((newName) => {
    section.setUnitsName(newName);
  }, [ section ]);
  
  const handleUnitPriceNameChange = useCallback((newName) => {
    section.setUnitPriceName(newName);
  }, [ section ]);
  
  const handleShowLabelsColumn = () => {
    section.usedBreakdownColumns
      .find(c => c.name === BreakdownColumns.LABELS)
      ?.setVisibility(true)
  }
  
  const handleTableContainerScroll = (left) => {
    if(left >= unpinnedColumnsWidth - 52)
      setShowActionsOnScroll(true);
    else if(showActionsOnScroll)
      setShowActionsOnScroll(false);
  }
  
  const ColumnSettings = useMemo(() => (
    () => <SectionColumnSettings section={section} />
  ), [ section ]);
  
  return (
    <EstimateTableSettingsProvider
      useExpandCell={useExpandCell}
      displayColumns={visibleBreakdownColumns}
      descriptionColumns={usedDescriptionColumns}
      visibleWorkTypes={visibleWorkTypes}
      allDescriptionColumnsHidden={allDescriptionColumnsHidden}
      // priceClassName={priceClassName}
      // descriptionClassName={descriptionClassName}
      // additionalDescriptionClassName={additionalDescriptionClassName}
      // labelsClassName={labelsClassName}
      showCombinedHours={showCombinedHours}
      materialsSection={materialFirst}
      emptyWorkTypeCells={hasMaterialCell ? (2 - visibleWorkTypes.length) : 0}
      // emptyMaterialCells={visibleWorkTypes.length > 2 ? visibleWorkTypes.length - 2 : 0}
      totalEstimateCells={materialFirst ? 2 : Math.max(hasMaterialCell ? 2 : 0, visibleWorkTypes.length)}
      showNumerationColumn={showNumerationColumn}
      showLabelsColumn={showBreakdownLabelsColumn}
      sectionUnitsName={unitsName}
      setSectionUnitsName={handleUnitsNameChange}
      sectionUnitPriceName={unitPriceName}
      setSectionUnitPriceName={handleUnitPriceNameChange}
      lastPinnedCellLeft={lastPinnedCellLeft}
      unpinnedColumnsWidth={unpinnedColumnsWidth}
      showActionsOnScroll={showActionsOnScroll}
      artificialLabelColumnWidth={artificialLabelColumnWidth}
      setArtificialLabelColumnWidth={setArtificialLabelColumnWidth}
      setShowLabelsColumn={handleShowLabelsColumn}
      workTypeColumnsMaxState={displayWorkTypeTwoValues}
      totalWorkloadColumnMaxState={displayWorkloadTwoValues}
      ColumnSettings={ColumnSettings}
    >
      <Grid item container>
        <TableContainer
          useAltBackground
          tableHeaderRef={headerRef}
          tableContainerIdentifier={tableContainerIdentifier}
          useExpandCell={useExpandCell}
          className={classnames(colClasses.root, "sectionContainer", isPdfPrinterView ? "w-full" : tableClasses.tableContainer)}
          onScroll={handleTableContainerScroll}
        >
          { children }
        </TableContainer>
      </Grid>
    </EstimateTableSettingsProvider>
  );
});

SectionContainer.propTypes = {
  section: object.isRequired,
  index: number.isRequired,
  headerRef: object,
  children: node.isRequired,
};
